import { useEffect } from "react";

interface PageMetadata {
  title: string;
  description: string;
  path: string;
  locale: "en" | "zh";
  alternates?: Array<{ language: string; path: string }>;
}

const PRODUCTION_SITE_URL = "https://lingoadv.netlify.app";
const SITE_URL = (
  process.env.REACT_APP_SITE_URL ||
  (process.env.NODE_ENV === "production" ? PRODUCTION_SITE_URL : "")
).replace(/\/$/, "");
const ROOT_TITLE = "PageBunny";
const ROOT_DESCRIPTION =
  "PageBunny adapts your private TXT, EPUB, and pasted readings to your English level and helps you save vocabulary.";

function ensureMetaDescription(): HTMLMetaElement {
  const existing = document.querySelector<HTMLMetaElement>(
    'meta[name="description"]',
  );
  if (existing) return existing;

  const element = document.createElement("meta");
  element.name = "description";
  document.head.appendChild(element);
  return element;
}

function ensureCanonical(): HTMLLinkElement {
  const existing = document.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (existing) return existing;

  const element = document.createElement("link");
  element.rel = "canonical";
  document.head.appendChild(element);
  return element;
}

function ensureOpenGraphMeta(property: string): HTMLMetaElement {
  const existing = document.querySelector<HTMLMetaElement>(
    `meta[property="${property}"]`,
  );
  if (existing) return existing;

  const element = document.createElement("meta");
  element.setAttribute("property", property);
  document.head.appendChild(element);
  return element;
}

export function usePageMetadata({
  title,
  description,
  path,
  locale,
  alternates = [],
}: PageMetadata): void {
  useEffect(() => {
    const descriptionElement = ensureMetaDescription();
    const canonicalElement = ensureCanonical();
    const existingStructuredData = document.getElementById(
      "pagebunny-marketing-structured-data",
    );
    const siteOrigin = SITE_URL || window.location.origin;
    const canonicalUrl = `${siteOrigin}${path}`;
    const openGraph = {
      "og:type": "website",
      "og:site_name": "PageBunny",
      "og:title": title,
      "og:description": description,
      "og:url": canonicalUrl,
    };

    document.title = title;
    document.documentElement.lang = locale === "zh" ? "zh-CN" : "en";
    descriptionElement.content = description;
    canonicalElement.href = canonicalUrl;
    const openGraphElements = Object.entries(openGraph).map(
      ([property, content]) => {
        const element = ensureOpenGraphMeta(property);
        element.content = content;
        return element;
      },
    );
    document
      .querySelectorAll('link[rel="alternate"][hreflang]')
      .forEach((element) => element.remove());
    alternates.forEach(({ language, path }) => {
      const element = document.createElement("link");
      element.rel = "alternate";
      element.hreflang = language;
      element.href = `${siteOrigin}${path}`;
      element.dataset.pagebunnyMarketing = "true";
      document.head.appendChild(element);
    });

    const structuredData = document.createElement("script");
    structuredData.id = "pagebunny-marketing-structured-data";
    structuredData.type = "application/ld+json";
    structuredData.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "PageBunny",
      applicationCategory: "EducationalApplication",
      operatingSystem: "Web browser",
      url: canonicalUrl,
      description,
      inLanguage: locale === "zh" ? "zh-CN" : "en",
    });
    existingStructuredData?.remove();
    document.head.appendChild(structuredData);

    return () => {
      document.title = ROOT_TITLE;
      document.documentElement.lang = "en";
      descriptionElement.content = ROOT_DESCRIPTION;
      canonicalElement.href = `${siteOrigin}/`;
      openGraphElements.forEach((element) => element.remove());
      document
        .querySelectorAll('link[rel="alternate"][hreflang]')
        .forEach((element) => element.remove());
      structuredData.remove();
    };
  }, [alternates, description, locale, path, title]);
}
